import { Box, IconButton, LinearProgress, Typography } from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import { makeStyles } from '@material-ui/core/styles';
import PropTypes from 'prop-types';
import { useEffect, useRef } from 'react';
import useEnrolledPalLaunch from 'hooks/useEnrolledPalLaunch';
import PaymentIframe from 'components/modules/service/PalGuestPayment/PaymentIframe';

const OPENING_TITLE = 'Opening read-only session…';

const useStyles = makeStyles((theme) => ({
  panel: {
    boxSizing: 'border-box',
    padding: theme.spacing(2),
    width: '100%',
  },
  header: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginBottom: theme.spacing(1),
  },
  headerIconButton: {
    borderRadius: '50%',
    height: 36,
    width: 36,
  },
  progress: {
    marginTop: theme.spacing(1),
  },
  title: {
    fontWeight: theme.typography.fontWeightMedium,
  },
}));

const EnrolledPalViewAsSession = ({
  fspId,
  onClose,
  onError,
  onSuccess,
  payerId,
  tab,
  userId,
}) => {
  const classes = useStyles();
  const regionRef = useRef(null);
  const { status, error, jwt, assertionBaseUrl } = useEnrolledPalLaunch({
    fspId,
    payerId,
    userId,
    preOpenedWindow: tab,
    readOnly: true,
  });

  useEffect(() => {
    regionRef.current?.focus();
  }, []);

  useEffect(() => {
    if (status === 'error') {
      onError();
      tab.close();
    }
  }, [onError, status, tab]);

  if (status === 'success' && jwt && assertionBaseUrl) {
    return (
      <PaymentIframe
        assertionBaseUrl={assertionBaseUrl}
        fspId={fspId}
        ifpAssertionVariant="enrolled"
        jwt={jwt}
        keepWindowFocused={false}
        onJwtDelivered={() => onSuccess({ jwt, assertionBaseUrl })}
        onPopupClosed={onClose}
        preOpenedPopup={tab}
      />
    );
  }

  const handleKeyDown = (event) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      onClose();
    }
  };

  return (
    <Box
      aria-busy={!error}
      aria-label="PAL View As session"
      className={classes.panel}
      onKeyDown={handleKeyDown}
      ref={regionRef}
      role="region"
      tabIndex={-1}
    >
      <Box className={classes.header}>
        <IconButton
          aria-label="Close"
          className={classes.headerIconButton}
          onClick={onClose}
          size="small"
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>
      <Box aria-live="polite" role="status">
        {error ? (
          <Typography color="error" variant="body2">
            {error}
          </Typography>
        ) : (
          <Typography className={classes.title} component="h2" variant="h6">
            {OPENING_TITLE}
          </Typography>
        )}
      </Box>
      {!error && (
        <LinearProgress
          aria-label={OPENING_TITLE}
          className={classes.progress}
        />
      )}
    </Box>
  );
};

EnrolledPalViewAsSession.propTypes = {
  fspId: PropTypes.number.isRequired,
  onClose: PropTypes.func.isRequired,
  onError: PropTypes.func.isRequired,
  onSuccess: PropTypes.func.isRequired,
  payerId: PropTypes.number.isRequired,
  tab: PropTypes.shape({
    close: PropTypes.func,
    closed: PropTypes.bool,
  }).isRequired,
  userId: PropTypes.number.isRequired,
};

export default EnrolledPalViewAsSession;
